/**
 * Route configuration validation.
 * Returns a list of human-readable errors. Validation is a warning only (user can save anyway).
 * Use across both new and edit route flows.
 */

import {
  hasActiveCalendarDays,
  resolveOptionBusinessHoursEnabled,
  resolveOptionDigit,
  sortIvrOptionEntries,
} from "./routePayloadNormalize";
import {
  isDeviceSentinel,
  parseDeviceUserId,
} from "../../utils/forwardDeviceDestinations";
import { hasDialableDestination } from "../../utils/forwardDestinations";

function getEnableBusinessHours(route) {
  if (route?.businessHoursEnabled === true || route?.businessHoursEnabled === false) {
    return route.businessHoursEnabled;
  }
  return hasActiveCalendarDays(route?.calendar);
}

function pushError(errors, message, section) {
  errors.push({ message, section });
}

/**
 * Validate a single call action (primary or off-hours) and push errors.
 * @param {object} actionContext - { action, number, recording, agentId, channel, notificationType }
 * @param {string} contextLabel - e.g. "Primary call action", "Off hours action", "Menu option (Press 1)"
 * @param {string} section - section id for UI highlighting
 */
function validateCallAction(actionContext, contextLabel, errors, section) {
  const {
    action,
    number,
    destinations,
    recording,
    agentId,
    channel,
    notificationType,
    twilanswerVmFallback,
  } = actionContext;
  if (!action) {
    pushError(errors, `${contextLabel}: You must select a call action (e.g. Forward, Play Recording).`, section);
    return;
  }
  const hasDestination = hasDialableDestination(destinations, number);
  switch (action) {
    case "forward":
      if (!hasDestination) pushError(errors, `${contextLabel}: Forward requires a destination number.`, section);
      break;
    case "forwardvwm":
      if (!hasDestination) pushError(errors, `${contextLabel}: Forward to Voicemail requires a destination number.`, section);
      if (!recording) pushError(errors, `${contextLabel}: Forward to Voicemail requires a voicemail recording.`, section);
      break;
    case "twilanswer_app": {
      const userId = isDeviceSentinel(number) ? parseDeviceUserId(number) : null;
      if (!userId) {
        pushError(
          errors,
          `${contextLabel}: Select a user whose Twilanswer app should ring.`,
          section
        );
      }
      if (!recording) pushError(errors, `${contextLabel}: Twilanswer App requires a voicemail recording.`, section);
      break;
    }
    case "dynamic_forward": {
      const backupIsNone = !number || number === "none";
      const needsRecording = backupIsNone || twilanswerVmFallback === true;
      if (needsRecording && !recording) {
        pushError(errors, `${contextLabel}: Dynamic Forward requires a voicemail recording when backup is None or Twilanswer Voicemail Fallback is enabled.`, section);
      }
      break;
    }
    case "send_to_vm":
      if (!recording) pushError(errors, `${contextLabel}: Send to Voicemail requires a voicemail recording.`, section);
      break;
    case "play_recording":
      if (!recording) pushError(errors, `${contextLabel}: Play Recording requires a recording to play.`, section);
      break;
    case "ai_agent":
      if (!agentId) pushError(errors, `${contextLabel}: ElevenLabs requires an Agent ID to be set.`, section);
      break;
    case "ring_browser":
    case "ivr":
      break;
    default:
      break;
  }
  if (notificationType && !channel) {
    pushError(errors, `${contextLabel}: A notification is selected but no channel is chosen. Please select a channel or remove the notification.`, section);
  }
}

/**
 * Validate notification at route level: if a notification type is set, channel must be set.
 */
function validateRouteLevelNotification(route, enableBusinessHours, errors) {
  if (!enableBusinessHours) {
    if (route?.primaryNotification && !route?.primaryChannel) {
      pushError(errors, "A notification is selected but no channel is chosen. Please select a channel or remove the notification.", "primary_notification");
    }
    return;
  }
  if (route?.primaryNotification && !route?.primaryChannel) {
    pushError(errors, "Business hours notification is selected but no channel is chosen. Please select a channel or remove the notification.", "primary_notification");
  }
  if (route?.offHoursNotification && !route?.offHoursChannel) {
    pushError(errors, "After hours notification is selected but no channel is chosen. Please select a channel or remove the notification.", "offhours_notification");
  }
}

function getIvrOptionDigit(opt, idx, optionKey = null) {
  return resolveOptionDigit(opt, optionKey, idx);
}

/**
 * Validate IVR menu (primary or off-hours).
 * @param {object} menu - ivrMenu or offHoursIvrMenu
 * @param {string} menuLabel - e.g. "Call Menu", "Call Menu (business hours)", "Call Menu (after hours)"
 * @param {string} section - section id for UI highlighting (e.g. 'ivr_menu', 'offhours_ivr_menu')
 * @param {string} primaryOptionSuffix - suffix for option's primary action (e.g. "" or " — business hours")
 * @param {string} offHoursOptionSuffix - suffix for option's off-hours action (e.g. " — after hours")
 */
function validateIvrMenu(menu, menuLabel, errors, section, primaryOptionSuffix = " — business hours", offHoursOptionSuffix = " — after hours") {
  if (!menu) return;
  const needMenuRecording = !menu.menu_recording || menu.menu_recording === "disabled";
  const needHangupRecording = !menu.hangup_recording || menu.hangup_recording === "disabled";
  if (needMenuRecording) pushError(errors, `${menuLabel}: A Menu Recording is required.`, section);
  if (needHangupRecording) pushError(errors, `${menuLabel}: A Hangup Recording is required.`, section);
  const optionsRaw = menu.options;
  const options = !optionsRaw
    ? []
    : Array.isArray(optionsRaw)
      ? optionsRaw
      : sortIvrOptionEntries(Object.entries(optionsRaw)).map(([, opt]) => opt);
  if (options.length === 0) {
    pushError(errors, `${menuLabel}: At least one menu option is required (e.g. Press 1, Press 2).`, section);
    return;
  }
  // No duplicate keys: each option must use a different number/key (1-9, 0, *, #)
  const digitToFirstIndex = new Map();
  const duplicateDigits = new Set();
  const optionEntries = Array.isArray(optionsRaw)
    ? optionsRaw.map((opt, idx) => [String(idx), opt])
    : sortIvrOptionEntries(Object.entries(optionsRaw));
  optionEntries.forEach(([optionKey, opt], idx) => {
    const digit = getIvrOptionDigit(opt, idx, optionKey);
    if (digitToFirstIndex.has(digit)) {
      duplicateDigits.add(digit);
    } else {
      digitToFirstIndex.set(digit, idx);
    }
  });
  duplicateDigits.forEach((digit) => {
    pushError(errors, `${menuLabel}: Two or more options use the same key (Press ${digit}). Each option must use a different number or key (1-9, 0, *, #).`, section);
  });
  // Helper: read option field from either camelCase or snake_case (API may return either)
  const optVal = (o, camelKey) => {
    const val = o?.[camelKey];
    if (val !== undefined && val !== null && val !== "") return val;
    const snake = camelKey.replace(/([A-Z])/g, "_$1").toLowerCase().replace(/^_/, "");
    return o?.[snake];
  };
  const optBool = (o, camelKey) => {
    const val = optVal(o, camelKey);
    return val === true || val?.BOOL === true;
  };

  options.forEach((opt, idx) => {
    const optionLabelBase = `${menuLabel}, option ${idx + 1} (${opt.name || "unnamed"})`;
    // When business hours enabled on option, primaryAction is the during-hours action; legacy "ivr" with no real action = require one
    const businessHoursOn = resolveOptionBusinessHoursEnabled(opt);
    const primaryAction = businessHoursOn && (!opt.primaryAction || opt.primaryAction === "ivr") ? null : (opt.primaryAction ?? opt.primary_action);
    const primaryNumber = optVal(opt, "primaryNumber");
    validateCallAction(
      {
        action: primaryAction,
        number: primaryNumber,
        destinations: optVal(opt, "primaryDestinations"),
        recording: optVal(opt, "primaryRecording"),
        agentId: optVal(opt, "primaryAgent"),
        channel: optVal(opt, "primaryChannel"),
        notificationType: optVal(opt, "primaryNotification"),
        twilanswerVmFallback: optBool(opt, "twilanswerVmFallback"),
      },
      primaryOptionSuffix ? optionLabelBase + primaryOptionSuffix : optionLabelBase,
      errors,
      section
    );
    const offHoursAction = opt.offHoursAction ?? opt.offhours_action;
    // If option-level business hours are enabled, after-hours action is required (mirror route-level behavior).
    if (businessHoursOn && !offHoursAction) {
      pushError(
        errors,
        `${optionLabelBase}: With Business Hours enabled on this option, you must set an after-hours call action.`,
        section
      );
    }
    if (offHoursAction) {
      validateCallAction(
        {
          action: offHoursAction,
          number: optVal(opt, "offHoursNumber"),
          destinations: optVal(opt, "offHoursDestinations"),
          recording: optVal(opt, "offHoursRecording"),
          agentId: optVal(opt, "offHoursAgent"),
          channel: optVal(opt, "offHoursChannel"),
          notificationType: optVal(opt, "offHoursNotification"),
          twilanswerVmFallback: optBool(opt, "offHoursTwilanswerVmFallback"),
        },
        optionLabelBase + offHoursOptionSuffix,
        errors,
        section
      );
    }
  });
}

/**
 * Main validation entry. Run before save (create or update).
 * @param {object} route - Current route state
 * @param {boolean} [enableBusinessHours] - If not provided, derived from route.calendar
 * @returns {{ errors: { message: string, section: string }[], isValid: boolean }}
 */
export function validateRoute(route, enableBusinessHours = undefined) {
  const errors = [];
  const afterHours = enableBusinessHours ?? getEnableBusinessHours(route);

  // --- Must have a configured phone number ---
  if (!route?.configured_phone || String(route.configured_phone).trim() === "") {
    pushError(errors, "You must select a phone number for this route.", "configured_phone");
  }

  // --- Call greeting: if enabled, must have a recording ---
  if (route?.callGreetingEnabled && !route?.callGreetingRecording) {
    pushError(errors, "Call Greeting is enabled but no recording is selected. Please select a recording or disable Call Greeting.", "call_greeting");
  }

  // --- Must have a call action ---
  if (!route?.primaryAction) {
    pushError(errors, "You must select a call action for incoming calls (e.g. Forward, Play Recording, Call Menu).", "primary_action");
  }

  // --- If after hours enabled, need both primary and off-hours actions ---
  if (afterHours) {
    if (!route?.primaryAction) {
      pushError(errors, "With after hours enabled, you must set a primary (business hours) call action.", "primary_action");
    }
    if (!route?.offHoursAction) {
      pushError(errors, "With after hours enabled, you must set an off hours call action.", "offhours_action");
    }
  }

  // --- Primary call action validation ---
  if (route?.primaryAction && route.primaryAction !== "ivr") {
    const primaryActionLabel = afterHours ? "Primary call action" : "Call action";
    validateCallAction(
      {
        action: route.primaryAction,
        number: route.primaryNumber,
        destinations: route.primaryDestinations,
        recording: route.primaryRecording,
        agentId: route.primaryAgent,
        channel: route.primaryChannel,
        notificationType: route.primaryNotification,
        twilanswerVmFallback: route.twilanswerVmFallback === true || route.twilanswerVmFallback?.BOOL === true,
      },
      primaryActionLabel,
      errors,
      "primary_action"
    );
  }

  // --- Off hours call action validation ---
  if (afterHours && route?.offHoursAction && route.offHoursAction !== "ivr") {
    validateCallAction(
      {
        action: route.offHoursAction,
        number: route.offHoursNumber,
        destinations: route.offHoursDestinations,
        recording: route.offHoursRecording,
        agentId: route.offHoursAgent,
        channel: route.offHoursChannel,
        notificationType: route.offHoursNotification,
        twilanswerVmFallback: route.offHoursTwilanswerVmFallback === true || route.offHoursTwilanswerVmFallback?.BOOL === true,
      },
      "Off hours call action",
      errors,
      "offhours_action"
    );
  }

  // --- Call Menu (IVR) validation ---
  if (route?.primaryAction === "ivr") {
    const primaryIvrLabel = afterHours ? "Call Menu (business hours)" : "Call Menu";
    const primaryOptionSuffix = afterHours ? " — business hours" : "";
    validateIvrMenu(route.ivrMenu, primaryIvrLabel, errors, "ivr_menu", primaryOptionSuffix, " — after hours");
  }
  if (route?.offHoursAction === "ivr") {
    validateIvrMenu(route.offHoursIvrMenu, "Call Menu (after hours)", errors, "offhours_ivr_menu", " — after hours", " — after hours");
  }

  // --- Notifications (channel required if notification type set) ---
  validateRouteLevelNotification(route, afterHours, errors);

  return {
    errors,
    isValid: errors.length === 0,
  };
}
